import React, { memo, useCallback, useMemo } from 'react';
import cn from 'classnames';
import useSWR from 'swr';
import queryString from 'query-string';
import { useTranslation } from 'next-i18next';

import { MatchCardsSkeleton } from 'components/skeletons/dashboard.component';
import { OrganizationOpportunityCard } from 'components/ComponentV2/OrganizationOpportunityCard';
import { Checkbox } from 'components/form';
import { get } from 'lib/utils/http';
import {
  OpportunitiesResponse,
  OpportunityAttributes,
} from 'lib/models/opportunity';
import { useSocietyGlobal } from 'lib/contexts/society-global';
import { parseArrayResponse } from 'lib/utils/parser';
import { API_ROUTES } from 'lib/api-routes';
import { TouchpointType } from 'lib/models/touchpoint';

import styles from '../CareerOpportunity.module.scss';

export interface FeaturedProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

const PER_PAGE_COUNT = 5;

export const Featured: React.FC<FeaturedProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: FeaturedProps) => {
    const [t] = useTranslation('career-opportunities');
    const { selectedTouchpoints, setSelectedTouchpoints } = useSocietyGlobal();

    const getTouchpointListKey = () => {
      const params = {
        featured: true,
        page: 1,
        per_page: PER_PAGE_COUNT,
        with_details: true,
      };
      const qs = queryString.stringify(params, {
        arrayFormat: 'bracket',
        skipEmptyString: true,
      });

      return `${API_ROUTES.CAMPAIGNS}?${qs}`;
    };

    const { data: touchpointResponse } = useSWR<OpportunitiesResponse>(
      getTouchpointListKey,
      get //not sending headers
    );
    const touchpoints = useMemo(() => {
      return parseArrayResponse<OpportunityAttributes>(touchpointResponse);
    }, [touchpointResponse]);

    const handleCheckboxChange = useCallback(
      (id: string, touchpointable_type: TouchpointType) => {
        setSelectedTouchpoints((prevSelected) => {
          const updatedSelected = prevSelected.some(
            (touchpoint) => touchpoint.id === id
          )
            ? prevSelected.filter((touchpoint) => touchpoint.id !== id)
            : [...prevSelected, { id, touchpointable_type }];

          return updatedSelected;
        });
      },
      [touchpoints]
    );

    const handleSelectAll = useCallback(() => {
      setSelectedTouchpoints((prevSelected) => {
        const areAllSelected = touchpoints.every(({ id }) =>
          prevSelected.some((tp) => tp.id === id)
        );

        if (areAllSelected) {
          return prevSelected.filter(
            (tp) => !touchpoints.some((touchpoint) => touchpoint.id === tp.id)
          );
        } else {
          const newTouchpoints = touchpoints.filter(
            ({ id }) => !prevSelected.some((tp) => tp.id === id)
          );

          return [
            ...prevSelected,
            ...newTouchpoints.map(({ id, touchpointable_type }) => ({
              id,
              touchpointable_type,
            })),
          ];
        }
      });
    }, [touchpoints]);

    const totalCount = touchpointResponse
      ? touchpointResponse?.meta?.total
      : undefined;

    const getOpportunityCard = (touchpoints: Array<OpportunityAttributes>) => {
      return (
        <div className={styles.touchpointcardList}>
          {touchpoints?.map((touchpoint) => {
            const { id, touchpointable_type } = touchpoint;
            return (
              <div key={id} className={styles['card-container']}>
                <OrganizationOpportunityCard
                  className={styles.touchpointcard}
                  key={id}
                  opportunity={touchpoint}
                  showFeaturedTag={false}
                />
                <Checkbox
                  className={styles.checkbox}
                  checked={selectedTouchpoints.some(
                    (selectedTouchpoint) => selectedTouchpoint.id === id
                  )}
                  onChange={() => handleCheckboxChange(id, touchpointable_type)}
                />
              </div>
            );
          })}
        </div>
      );
    };

    const contentClassNames = cn(
      {
        [styles.container]: true,
        [styles.hide]: totalCount === 0,
      },
      className
    );

    return (
      <div className={contentClassNames} style={style} data-testid="Featured">
        <div className={styles['title-container']}>
          <p className={styles.title}>{t('featured-title')}</p>
          <p className={styles['select-all']} onClick={handleSelectAll}>
            {t('select-all')}
          </p>
        </div>
        {totalCount ? getOpportunityCard(touchpoints) : null}
        {totalCount === undefined ? <MatchCardsSkeleton /> : null}
      </div>
    );
  }
);

Featured.displayName = 'Featured';
